import React, { ReactElement, useState, ChangeEvent } from 'react';
import styled from 'styled-components';
import {
  StyledBlueButton,
  StyledRedButton,
  StyledButton,
} from '../../Button/Button';
import CloseIcon from './x_close_icon.svg';
import { TextInput } from 'carbon-components-react';
import { Loader } from '../../Loader/Loader';

type Props = {
  headline: string;
  confirmButtonText: string;
  updateNameCallback: ({
    name,
    familyName,
  }: {
    name: string;
    familyName?: string | null;
  }) => void;
  dismissButtonText: string;
  loading: boolean;
  data: { name: string; familyName?: string | null };
  dismissButtonCallback: () => void;
  dismissToPath?: string | null;
  confirmButtonColor: 'blue' | 'red';
};

const MAX_NAME_LENGTH = 60;

export const EditName = ({
  headline,
  data,
  loading,
  confirmButtonText,
  confirmButtonColor,
  updateNameCallback,
  dismissToPath,
  dismissButtonText,
  dismissButtonCallback,
}: Props): ReactElement | null => {
  const dismissDialog: React.MouseEventHandler<HTMLImageElement> = (e) => {
    e.preventDefault();
    dismissButtonCallback();
  };

  const [nameValue, setNameValue] = useState(data.name);
  const [familyNameValue, setFamilyNameValue] = useState(data.familyName);

  const nameValueLength = nameValue.length;
  const familyNameValueLength = familyNameValue?.length ?? 0;

  const confirmDialog: React.MouseEventHandler<HTMLImageElement> = async (
    e
  ) => {
    e.preventDefault();
    updateNameCallback({ name: nameValue, familyName: familyNameValue });
  };
  return (
    <ConfirmationTakeover>
      <ConfirmationWrapper>
        {loading ? (
          <LoaderWrapper>
            <Loader />
          </LoaderWrapper>
        ) : (
          <>
            <CloseIconImg
              data-testid={'dismiss-x-icon'}
              onClick={dismissDialog}
              src={CloseIcon}
              alt="Close this dialog"
            ></CloseIconImg>
            <Heading>{headline}</Heading>
            <SelectRow>
              <StyledTextInput
                invalidText={`Name length should be between 1 and ${MAX_NAME_LENGTH} characters`}
                data-testid={'name-input'}
                maxLength={MAX_NAME_LENGTH}
                onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                  const newNameVal = e.target.value;
                  setNameValue(newNameVal);
                }}
                helperText={
                  nameValueLength + ' / ' + MAX_NAME_LENGTH + ' characters'
                }
                labelText="First Name"
                disabled={false}
                type="text"
                id={`Edit Name`}
                size={undefined}
                invalid={
                  nameValueLength < 1 || nameValueLength > MAX_NAME_LENGTH
                }
                min={0}
                value={nameValue}
              />
            </SelectRow>
            <SelectRow>
              <StyledTextInput
                data-testid={'family-name-input'}
                invalidText={`Family name length should be no more than ${MAX_NAME_LENGTH} characters`}
                onChange={(e: ChangeEvent<HTMLInputElement>): void => {
                  const newFamilyNameVal = e.target.value;
                  setFamilyNameValue(newFamilyNameVal);
                }}
                maxLength={MAX_NAME_LENGTH}
                helperText={
                  familyNameValueLength +
                  ' / ' +
                  MAX_NAME_LENGTH +
                  ' characters'
                }
                labelText="Family Name"
                disabled={false}
                type="text"
                id={`Edit Family Name`}
                size={undefined}
                invalid={familyNameValueLength > MAX_NAME_LENGTH}
                min={0}
                value={familyNameValue}
              />
            </SelectRow>
            <ButtonWrapper>
              <ConfirmButton
                confirmButtonColor={confirmButtonColor}
                data-testid={'confirm-button'}
                disabled={
                  loading ||
                  nameValueLength <= 0 ||
                  nameValueLength > MAX_NAME_LENGTH ||
                  familyNameValueLength > MAX_NAME_LENGTH
                }
                onClick={confirmDialog}
                kind="secondary"
                size="default"
                tabIndex={0}
                type="button"
              >
                {confirmButtonText}
              </ConfirmButton>
              <DismissButton
                data-testid={'dismiss-button'}
                onClick={dismissDialog}
                kind="primary"
                size="default"
                tabIndex={0}
                type="button"
                href={dismissToPath}
              >
                {dismissButtonText}
              </DismissButton>
            </ButtonWrapper>
          </>
        )}
      </ConfirmationWrapper>
    </ConfirmationTakeover>
  );
};

export const StyledConfirmButton = styled((props) => (
  <StyledBlueButton {...props} />
))`
  margin: 0;
  font-weight: bold;
  width: 100%;
  margin: 10px 0px;
`;

export const StyledTextInput = styled((props) => (
  <TextInput {...props} />
)).attrs((props) => ({
  $isDirty: props.$isDirty || false,
}))<{ $isDirty?: boolean }>`
  display: flex;
  flex-direction: row;
  justify-content: center;
  input[type='text'] {
    color: ${(props): string => {
      if (props.invalid) return props.theme.red;
      return props.$isDirty ? props.theme.inspireCobalt : 'inherit';
    }};
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

export const Padder = styled.div`
  margin-bottom: ${(props): string => props.theme.contentMarginBottom};
`;

export const ButtonPadder = styled.div`
  margin-bottom: 10px;
`;

const Heading = styled.h2`
  font-weight: normal;
  font-size: ${(props): string => props.theme.l};
  margin: 50px 0px;
`;

// We use z-index to make sure the takeover window sits above any other window content
const ConfirmationTakeover = styled.div`
  display: flex;
  z-index: ${(props): string => props.theme.confirmationTakeover};
  justify-content: center;
  align-items: center;
  background: rgba(57, 57, 57, 0.3);
  width: 100vw;
  height: 100vh;
  top: 0;
  left: 0;
  position: fixed;
`;

const LoaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
`;

const ConfirmationWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  text-align: center;
  padding: 20px 50px;
  border: 1px solid ${(props): string => props.theme.grey};
  border-radius: 15px;
  background: ${(props): string => props.theme.white};
  height: 500px;
  width: 422px;
  position: relative;
`;

const ConfirmButton = styled(({ confirmButtonColor, ...rest }) => {
  type Options = {
    blue: JSX.Element;
    red: JSX.Element;
  };

  const options: Options = {
    blue: <StyledBlueButton {...rest} />,
    red: <StyledRedButton {...rest} />,
  };

  return (
    options[confirmButtonColor as keyof Options] || (
      <StyledBlueButton {...rest} />
    )
  );
})`
  width: 100%;
  height: 48px;
`;

const DismissButton = styled((props) => <StyledButton {...props} />)`
  width: 100%;
  height: 48px;
  margin: 10px 0px 50px 0px;
`;

const CloseIconImg = styled.img`
  position: absolute;
  right: 20px;
  cursor: pointer;
`;

const SelectRow = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: 20px;
  text-align: left;
`;
